import { toLocal } from "@letsdoit/due-parser";
import type { Deadline, Due } from "../api/types";

export type Lang = "en" | "zh";
export type DateTone = "overdue" | "today" | "tomorrow" | "week" | "later";

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` → days since epoch (UTC calendar arithmetic, no timezone involved). */
export function dayNum(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Math.floor(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / DAY_MS);
}

export function dayStr(n: number): string {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

export const addDays = (day: string, n: number) => dayStr(dayNum(day) + n);

/** ISO weekday (1 = Monday … 7 = Sunday). */
export function isoWeekday(day: string): number {
  const wd = new Date(dayNum(day) * DAY_MS).getUTCDay();
  return wd === 0 ? 7 : wd;
}

/** First day of the week containing `day`, for a user `start_day` (ISO weekday). */
export function weekStart(day: string, startDay = 1): string {
  const diff = (isoWeekday(day) - startDay + 7) % 7;
  return addDays(day, -diff);
}

export function todayIn(tz: string, now = new Date()): string {
  try {
    return toLocal(now, tz).slice(0, 10);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** The local day and time (`HH:MM`, or null for full-day dues) a due falls on for the user. */
export function dueLocal(due: Pick<Due, "date">, tz: string): { day: string; time: string | null } {
  let local = due.date;
  if (local.endsWith("Z")) {
    try {
      local = toLocal(new Date(local), tz);
    } catch {
      // keep UTC
    }
  }
  return { day: local.slice(0, 10), time: local.length > 10 ? local.slice(11, 16) : null };
}

export function formatTime(time: string, timeFormat: number): string {
  if (timeFormat !== 1) return time;
  const [h = 0, m = 0] = time.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${suffix}` : `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

const EN_WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const EN_WEEKDAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ZH_WEEKDAYS = ["星期一", "星期二", "星期三", "星期四", "星期五", "星期六", "星期日"];
const ZH_WEEKDAYS_SHORT = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
const EN_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EN_MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function weekdayName(day: string, lang: Lang, short = false): string {
  const i = isoWeekday(day) - 1;
  if (lang === "zh") return (short ? ZH_WEEKDAYS_SHORT : ZH_WEEKDAYS)[i] ?? "";
  return (short ? EN_WEEKDAYS_SHORT : EN_WEEKDAYS)[i] ?? "";
}

export function monthName(month: number, lang: Lang, long = false): string {
  if (lang === "zh") return `${month}月`;
  return (long ? EN_MONTHS_LONG : EN_MONTHS)[month - 1] ?? "";
}

/** "Oct 21" / "10月21日", with the year when it differs from `today`'s. */
export function shortDate(day: string, today: string, lang: Lang): string {
  const [y, m, d] = day.split("-").map(Number);
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  if (lang === "zh") return `${sameYear ? "" : `${y}年`}${m}月${d}日`;
  return `${monthName(m ?? 1, "en")} ${d}${sameYear ? "" : ` ${y}`}`;
}

export function relativeDayName(day: string, today: string, lang: Lang): string {
  const diff = dayNum(day) - dayNum(today);
  if (diff === 0) return lang === "zh" ? "今天" : "Today";
  if (diff === 1) return lang === "zh" ? "明天" : "Tomorrow";
  if (diff === -1) return lang === "zh" ? "昨天" : "Yesterday";
  if (diff > 1 && diff < 7) return weekdayName(day, lang);
  return shortDate(day, today, lang);
}

export function dayTone(
  day: string,
  today: string,
  time: string | null = null,
  nowTime: string | null = null,
): DateTone {
  const diff = dayNum(day) - dayNum(today);
  if (diff < 0) return "overdue";
  if (diff === 0) return time && nowTime && time < nowTime ? "overdue" : "today";
  if (diff === 1) return "tomorrow";
  if (diff < 7) return "week";
  return "later";
}

export interface DateLabelOptions {
  today: string;
  tz: string;
  lang: Lang;
  timeFormat?: number;
  /** Current local `HH:MM`, to mark timed dues earlier today as overdue. */
  nowTime?: string | null;
}

/** Due chip text and color tone, the way Todoist shows it in task rows. */
export function dueLabel(due: Due, o: DateLabelOptions): { text: string; tone: DateTone; day: string } {
  const { day, time } = dueLocal(due, o.tz);
  const name = relativeDayName(day, o.today, o.lang);
  const text = time ? `${name} ${formatTime(time, o.timeFormat ?? 0)}` : name;
  return { text, tone: dayTone(day, o.today, time, o.nowTime ?? null), day };
}

export function deadlineLabel(deadline: Deadline, o: DateLabelOptions): { text: string; tone: DateTone } {
  return { text: relativeDayName(deadline.date, o.today, o.lang), tone: dayTone(deadline.date, o.today) };
}

/** Upcoming / Today group header: "Oct 21 · Today · Wednesday" / "10月21日 · 今天 · 星期三". */
export function dayHeader(day: string, today: string, lang: Lang): string {
  const parts = [shortDate(day, today, lang)];
  const diff = dayNum(day) - dayNum(today);
  if (diff === 0) parts.push(lang === "zh" ? "今天" : "Today");
  else if (diff === 1) parts.push(lang === "zh" ? "明天" : "Tomorrow");
  parts.push(weekdayName(day, lang));
  return parts.join(" · ");
}

/** Month grid (6 weeks) starting on the user's week start. */
export function monthGrid(year: number, month: number, startDay = 1): string[] {
  const first = `${year}-${String(month).padStart(2, "0")}-01`;
  const start = weekStart(first, startDay);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}
